import React from "react";
import { observer } from "mobx-react";
import styles from "./ProjectPortfoliosList.module.scss";
import { columns } from "./columns";
import { saveAgGridColumnSize } from "modules/common/services/table/size-storage";
import { DefaultAgGridOptions, rowIdBy } from "modules/common/services/table/helpers";
import { RemoveConfirmation } from "modules/common/components/form/RemoveConfirmation";
import { agGridEntityContextMenu } from "modules/common/components/table/EntityContextMenu";
import { ProjectPortfoliosListType, ProjectPortfolioList } from "../../models/project-portfolio-list";
import { ProjectPortfolioSnapshotType, formatProjectPortfolio } from "../../models/project-portfolio";
import copyToClipboard from "copy-to-clipboard";
import { MAX_INT } from "modules/common/constants";

import {
  GridOptions,
  GetContextMenuItemsParams,
  CellContextMenuEvent,
  RowSelectedEvent,
  GridReadyEvent,
  RowDoubleClickedEvent,
} from "ag-grid-community";
import { GridTheme } from "modules/common/components/table/GridTheme";
import { AgGridReact } from "ag-grid-react";
import { agGridLocale } from "modules/root/services/locale";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon";

const TABLE_NAME = ProjectPortfolioList.name;
const sizeSaver = saveAgGridColumnSize(TABLE_NAME);
const config = columns(TABLE_NAME);

export const ProjectPortfoliosList = observer(
  class extends React.Component<ProjectPortfoliosListProps> {
    private options: GridOptions = DefaultAgGridOptions();

    render() {
      const { store, onSelected, onClicked } = this.props;

      return (
        <RemoveConfirmation<ProjectPortfolioSnapshotType>
          observation={store.data}
          what={this.removeLabel}
          onConfirmed={this.removeStatus}
          render={({ confirmRemoving }) => {
            const getContextMenuItems = ({ node }: GetContextMenuItemsParams) => {
              // меню вызвано не на строке — показывать нечего
              if (!node) {
                return [];
              }

              return agGridEntityContextMenu({
                onCopy: () => copyToClipboard(formatProjectPortfolio(node.data), { format: "text/plain" }),
                onChange: () => onSelected(getRowNodeId(node.data)),
                onRemove: () => confirmRemoving(node.data),
              });
            };

            return (
              <>
                <GridTheme bordered={true} className={`designed ${styles.grid}`}>
                  <AgGridReact
                    rowStyle={{
                      borderBottom: "1 px solid #EDF0F2",
                      fontFamily: "GothamPro, Arial, sans-serif",
                      color: "#00273D",
                      height: "32px",
                      fontSize: "12px",
                    }}
                    rowSelection="single"
                    suppressCellFocus={true}
                    enableCellTextSelection={true}
                    columnDefs={config}
                    rowData={store.data}
                    localeText={agGridLocale}
                    onRowDoubleClicked={this.onRowSelected}
                    onRowSelected={({ data, node }: RowSelectedEvent) => {
                      if (!node.isSelected()) {
                        return;
                      }

                      onClicked && onClicked(getRowNodeId(data));
                    }}
                    getRowId={getRowId}
                    getContextMenuItems={getContextMenuItems}
                    onGridReady={this.onGridReady}
                    onCellContextMenu={this.onCellContextMenu}
                    onSortChanged={store.sorter.resorted}
                    onColumnResized={sizeSaver}
                    gridOptions={this.options}
                    rowBuffer={MAX_INT}
                  />
                </GridTheme>
                <div className={styles.cardList}>
                  {store.data.map((row) => {
                    return (
                      <div key={row.id} onClick={() => onClicked && onClicked(row.id)}>
                        <div className={styles.card}>
                          <div className={styles.mid}>
                            <div className={styles.label}>Наименование:</div>
                            <div className={styles.row}>
                              {row.name}
                              {row.hasMainBankDetails && (
                                <GeneralIcon
                                  type="general-star-fill"
                                  size={12}
                                  title={`Счёт на оплату по умолчанию: ${row.mainBankDetailsName ?? ""}`}
                                  style={{ color: "#ea561e", marginLeft: "6px" }}
                                />
                              )}
                            </div>

                            {row.description && (
                              <>
                                <div className={styles.label}>Описание:</div>
                                <div className={styles.row}>{row.description}</div>
                              </>
                            )}
                          </div>
                          <div className={styles.edit}>
                            <PlanrButton
                              type="lightblueish"
                              icon="general-edit"
                              size="small"
                              onClick={() => {
                                this.props.onSelected(row.id);
                              }}
                            />
                            <PlanrButton
                              type="neutral"
                              icon={"general-trash"}
                              size="small"
                              style={{ marginLeft: "0px", marginTop: "5px", color: "#e31818" }}
                              onClick={() => {
                                confirmRemoving(row);
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            );
          }}
        />
      );
    }

    removeLabel = (portfolio: ProjectPortfolioSnapshotType) => `организацию ${portfolio.name}`;

    removeStatus = async (status: ProjectPortfolioSnapshotType) => {
      const { store, onRemoved } = this.props;
      const success = await store.delete(status);
      success && onRemoved();
    };

    onCellContextMenu = ({ node }: CellContextMenuEvent) => {
      node && node.setSelected(true, true);
    };

    onRowSelected = ({ data }: RowDoubleClickedEvent) => {
      const { onSelected } = this.props;
      onSelected(getRowNodeId(data));
    };

    onGridReady = async ({ api }: GridReadyEvent) => {
      const { sorter } = this.props.store;
      setTimeout(() => api.applyColumnState(sorter.agGridSortState));
    };
  }
);

const getRowNodeId = (e: ProjectPortfolioSnapshotType) => e.id;
const getRowId = rowIdBy(getRowNodeId);

interface ProjectPortfoliosListProps {
  onRemoved: () => void;
  onSelected: (id: string) => void;
  onClicked?: (id: string) => void;
  store: ProjectPortfoliosListType;
}
